import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EVENTS as E } from '../../core/constants';
import { useSendEvents } from '../../core/hooks';
import { setAppLanguage } from '../../locales';
import { selectEnFlagVariant } from '../../core/store/locale';
import { selectUserLocation } from '../../core/store/signup';
import {
  flagUrl,
  getActiveLanguageItem,
  getAllLanguageItems,
  getLanguageSwitchUrl,
  getSuggestedLanguageItems,
  resolveEnFlagVariant,
  type LanguageItem,
} from '../../locales/languageConfig';
import { CloseIcon } from '../icons';
import * as S from './styled';

interface LanguageModalProps {
  onClose: () => void;
  screenId: string;
}

export const LanguageModal = ({ onClose, screenId }: LanguageModalProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const enFlag = resolveEnFlagVariant(useSelector(selectEnFlagVariant));
  const userLocation = useSelector(selectUserLocation);
  const sendEvents = useSendEvents({ screenId });
  const [loadingLocale, setLoadingLocale] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const loadTracked = useRef(false);

  useEffect(() => {
    const { body, documentElement: html } = document;
    const prevBody = body.style.overflow;
    const prevHtml = html.style.overflow;
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (loadTracked.current) return;
    loadTracked.current = true;
    sendEvents('language_modal__screen__load', { [E.SL]: 'language_modal' });
  }, [sendEvents]);

  const active = getActiveLanguageItem(i18n.language, enFlag);
  // Suggestions follow the browser, not the current choice, so the list stays put after a pick
  const suggested = getSuggestedLanguageItems(
    navigator.language,
    userLocation,
    resolveEnFlagVariant(null),
  );
  const all = getAllLanguageItems(new Set(suggested.map((item) => item.locale)));

  const handlePick = async (item: LanguageItem) => {
    if (loadingLocale) return;
    setLoadingLocale(item.locale);
    try {
      await setAppLanguage(item.locale);
      // A link language (/de/..., /at/de/..., ?lang=de) is what a reload reads, so it follows
      const { pathname, search, hash } = window.location;
      const nextUrl = getLanguageSwitchUrl(pathname, search, hash, item.locale);
      if (nextUrl) {
        navigate(nextUrl, { replace: true });
      }
      if (item !== active) {
        sendEvents('language_modal_language_picker__click', { [E.OS]: item.locale });
      }
      onClose();
    } catch (error) {
      // The modal stays open with the buttons enabled again, so the user can retry or close it
      console.error('Language switch failed:', error);
    } finally {
      setLoadingLocale(null);
    }
  };

  const renderItem = (item: LanguageItem) => (
    <S.LangButton
      key={item.locale}
      type="button"
      $active={item === active}
      disabled={!!loadingLocale}
      onClick={() => handlePick(item)}
    >
      <S.Flag src={flagUrl(item.flagFile)} alt="" />
      <S.LangLabel>{item.label}</S.LangLabel>
    </S.LangButton>
  );

  return createPortal(
    <S.Overlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <S.Panel
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="language-modal-title"
      >
        <S.HeadRow>
          <S.Title id="language-modal-title">{t('select_your_language')}</S.Title>
          <S.CloseBtn type="button" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </S.CloseBtn>
        </S.HeadRow>
        <S.Scroll>
          <S.Section>
            <S.SectionTitle>{t('suggested_for_you')}</S.SectionTitle>
            {suggested.map(renderItem)}
          </S.Section>
          <S.Section>
            <S.SectionTitle>{t('all_languages')}</S.SectionTitle>
            {all.map(renderItem)}
          </S.Section>
        </S.Scroll>
      </S.Panel>
    </S.Overlay>,
    document.body,
  );
};
